import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CustomNotification } from 'common/components';

import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';

import * as qaSpecServices from 'services/qaSpec';
import { forwardTo } from 'utils/common/route';
import { getQaPackagingTabDataQueryKey } from 'pages/qa-spec/components/qa-packaging/queries';

export const qaPetFoodKeys = {
  detail: (productId) => ['qa-spec', parseInt(productId), 'pet-food'],
  snapshot: (productId) => [
    'pim',
    'snapshot',
    parseInt(productId),
    'qa-pet-food',
  ],
};

export const useGetQaPetFood = (productId) => {
  return useQuery({
    queryKey: qaPetFoodKeys.detail(productId),
    queryFn: async () => {
      const response = await qaSpecServices.getQaSpecDataPetFood({ productId });
      if (response?.isSuccess) {
        return response?.data;
      }
      CustomNotification.error(response?.message ?? 'Something went wrong');
      forwardTo('/products');
    },
    enabled: Boolean(productId),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

export const useGetSnapshotQaPetFood = (productId) => {
  const isToggleSnapshot = useCheckSnapshotForRetailer();

  return useQuery({
    queryKey: qaPetFoodKeys.snapshot(productId),
    queryFn: async () => {
      const response = await qaSpecServices.getQaPetFoodSnapshot({ productId });
      if (response?.isSuccess) {
        return response?.data;
      }
      CustomNotification.error(response?.message ?? 'Something went wrong');
      forwardTo('/products');
    },
    enabled: Boolean(productId && isToggleSnapshot),
    staleTime: 0,
  });
};

export const useSaveQaPetFood = (productId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: qaSpecServices.saveQaSpecDataPetFood,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        queryClient.invalidateQueries({
          queryKey: qaPetFoodKeys.detail(productId),
        });
        queryClient.invalidateQueries({
          queryKey: getQaPackagingTabDataQueryKey(productId),
        });
        CustomNotification.success('Update Pet Food data successfully!');
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong');
      }
    },
    onError: (error) => {
      CustomNotification.error('Failed to save data');
    },
  });
};

export const useGetQaSpecPetFoodFormDefine = ({ productId }) => {
  const { getRequiredRule } = useCheckPetFoodQasRequiredField({ productId });

  const geometriesFormDefine = [
    {
      name: 'shape',
      label: 'Shape',
      fieldPath: 'qaSpecification.petFood.geometries.shape',
      type: 'text',
      rules: [
        getRequiredRule({
          fieldName: 'geometries.shape',
          displayName: 'Shape',
        }),
      ],
    },
    {
      name: 'dimension',
      label: 'H x W x D',
      fieldPath: 'qaSpecification.petFood.geometries.dimension',
      type: 'text',
      rules: [
        getRequiredRule({
          fieldName: 'geometries.dimension',
          displayName: 'H x W x D',
        }),
        {
          validator(_, value) {
            //* REGEX : (N)x(N)x(N)
            //* 3 group N with x between
            //* N : match number, accept '.' : \d*\.?\d+?
            const regex = /^(\d*\.?\d+?)x(\d*\.?\d+?)x(\d*\.?\d+?)$/g;

            const matched = regex.test(value);
            if (matched) {
              return Promise.resolve();
            }

            return Promise.reject(new Error('Invalid format'));
          },
        },
      ],
    },
    {
      name: 'weight',
      label: 'Weight',
      fieldPath: 'qaSpecification.petFood.geometries.weight',
      type: 'text',
      rules: [
        getRequiredRule({
          fieldName: 'geometries.weight',
          displayName: 'Weight',
        }),
      ],
    },
    {
      name: 'variance',
      label: 'Variance',
      fieldPath: 'qaSpecification.petFood.geometries.variance',
      type: 'text',
      rules: [
        getRequiredRule({
          fieldName: 'geometries.variance',
          displayName: 'Variance',
        }),
      ],
    },
  ];

  const additionalInfoFormDefine = useMemo(() => {
    const layout = {
      labelCol: { xxl: 18, span: 12 },
      wrapperCol: { xxl: 6, span: 12 },
    };
    return [
      [
        { rowConfig: { gutter: [15, 15] } },
        [
          {
            colProps: {
              span: 24,
            },
          },
          {
            name: 'humanGrade',
            label: 'Human Grade',
            fieldPath: 'qaSpecification.petFood.humanGrade',
            type: 'select',
            ...layout,
            selectProps: {
              inputProps: {
                allowClear: true,
              },
              options: [
                { displayName: 'Yes', value: 'Yes' },
                { displayName: 'No', value: 'No' },
              ],
            },
          },
          {
            name: 'containsPalmOil',
            label: 'Contains Palm Oil',
            fieldPath: 'qaSpecification.petFood.containsPalmOil',
            type: 'select',
            ...layout,
            selectProps: {
              inputProps: {
                allowClear: true,
              },
              options: [
                { displayName: 'Yes', value: 'Yes' },
                { displayName: 'No', value: 'No' },
              ],
            },
          },
          {
            name: 'ingredientsOutsideUs',
            label: 'Ingredients in product from outside the United States',
            fieldPath: 'qaSpecification.petFood.ingredientsOutsideUs',
            type: 'select',
            ...layout,
            selectProps: {
              inputProps: {
                allowClear: true,
              },
              options: [
                { displayName: 'Yes', value: 'Yes' },
                { displayName: 'No', value: 'No' },
              ],
            },
          },
        ],
      ],
    ];
  }, []);

  return {
    geometries: geometriesFormDefine,
    additionalInfo: additionalInfoFormDefine,
  };
};

export const useCheckPetFoodQasRequiredField = ({ productId }) => {
  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const checkQasRequiredField = (fieldName) => {
    if (!requiredFields?.length) return false;

    if (fieldName === 'usageInstructions')
      return requiredFields.includes(
        `qaSpecification.qaSpecPackaging.usage`.toLowerCase()
      );

    const isNormalRequired = requiredFields.includes(
      `qaSpecification.qaSpecPetFood.${fieldName}`.toLowerCase()
    );

    return isNormalRequired;
  };

  const getRequiredRule = ({ fieldName, displayName }) => {
    const isQasRequiredField = checkQasRequiredField(fieldName);

    return {
      required: isQasRequiredField,
      message: `${displayName || 'This field'} is required`,
    };
  };

  return { getRequiredRule };
};
